import { NavLink, Outlet, useLocation } from "react-router-dom";
import { PropsWithChildren, ReactNode } from "react";
import { cn } from "../../utils/cn";
import { Button } from "../ui/Button";
import { useAuthStore } from "../../store/auth";
import { apiClient } from "../../lib/apiClient";
import { toast } from "sonner";

interface NavItem {
  label: string;
  to: string;
  icon?: ReactNode;
  roles?: string[];
}

const navItems: NavItem[] = [
  { label: "Overview", to: "/dashboard/overview" },
  { label: "Agents", to: "/dashboard/agents", roles: ["owner", "admin", "manager"] },
  { label: "Widgets", to: "/dashboard/widgets", roles: ["owner", "admin", "manager", "agent"] },
  { label: "Chats", to: "/dashboard/chats", roles: ["owner", "admin", "manager", "agent"] },
  { label: "Analytics", to: "/dashboard/analytics", roles: ["owner", "admin", "manager"] }
];

const Logo = () => (
  <div className="flex items-center gap-2 text-lg font-semibold text-slate-100">
    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand text-lg font-bold text-white">
      RW
    </span>
    <div>
      <p>RWASupport</p>
      <span className="text-xs text-slate-400">Agent Workspace</span>
    </div>
  </div>
);

const SidebarLink = ({ to, label }: { to: string; label: string }) => {
  const location = useLocation();
  const isActive = location.pathname.startsWith(to);
  return (
    <NavLink
      to={to}
      className={cn(
        "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition",
        isActive ? "bg-slate-800 text-white" : "text-slate-400 hover:bg-slate-800/70 hover:text-white"
      )}
    >
      {label}
    </NavLink>
  );
};

export const DashboardLayout = ({ children }: PropsWithChildren) => {
  const agent = useAuthStore((state) => state.agent);
  const reset = useAuthStore((state) => state.reset);

  const handleLogout = async () => {
    try {
      await apiClient.post("/auth/logout");
    } catch {
      // ignore
    }
    reset();
    toast.success("Signed out");
    window.location.href = "/login";
  };

  const filteredNav = navItems.filter((item) => {
    if (!item.roles || !agent) return true;
    return item.roles.includes(agent.role);
  });

  return (
    <div className="flex min-h-screen bg-slate-950 text-slate-100">
      <aside className="hidden w-64 border-r border-slate-900 bg-slate-950/70 p-6 md:flex md:flex-col">
        <Logo />
        <div className="mt-8 space-y-1">
          {filteredNav.map((item) => (
            <SidebarLink key={item.to} to={item.to} label={item.label} />
          ))}
        </div>
        <div className="mt-auto rounded-lg border border-slate-800 bg-slate-900/60 p-4 text-xs text-slate-400">
          <p className="font-medium text-slate-200">{agent?.name}</p>
          <p className="capitalize text-slate-400">{agent?.role} account</p>
        </div>
      </aside>
      <div className="flex flex-1 flex-col">
        <header className="flex items-center justify-between border-b border-slate-900 bg-slate-950/80 px-6 py-4 backdrop-blur">
          <div className="md:hidden">
            <Logo />
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden text-sm text-slate-400 md:block">
              {agent ? `Signed in as ${agent.email}` : ""}
            </span>
            <Button variant="outline" size="sm" onClick={handleLogout}>
              Logout
            </Button>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto bg-slate-950/80 px-4 py-6 md:px-8">
          <div className="mx-auto max-w-6xl space-y-6">{children ?? <Outlet />}</div>
        </main>
      </div>
    </div>
  );
};
